import React from "react";
import { Stack, Typography, Divider, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import BusinessIcon from "@mui/icons-material/Business";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createBranch, updateBranch } from "../redux/actions/branchActions";
import { branchActions } from "../redux/slices/branch";
import { cleanFormData } from "../helpers/formHelpers";

import { BranchResponseDto } from "../shared/dtos/branch/BranchResponseDto";
import { BranchRequestDto } from "../shared/dtos/branch/BranchRequestDto";

interface CreateOrUpdateBranchFormProps {
  branch?: BranchResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateBranchForm: React.FC<CreateOrUpdateBranchFormProps> = ({
  branch = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { school } = useAppSelector((state) => state.school);

  const addressSchema = Yup.object().shape({
    streetLine1: Yup.string().required("Street Line 1 is required"),
    streetLine2: Yup.string().nullable(),
    city: Yup.string().required("City is required"),
    stateOrProvince: Yup.string().required("State/Province is required"),
    postalCode: Yup.string().required("Postal Code is required"),
    country: Yup.string().required("Country is required"),
  });

  const branchSchema = Yup.object().shape({
    name: Yup.string().required("Branch name is required"),
    branchCode: Yup.string().required("Branch code is required"),
    licenseKey: isEditing
      ? Yup.string().nullable()
      : Yup.string().required("Required"),
    principalName: Yup.string().required("Principal's name is required"),
    contactEmail: Yup.string()
      .email("Invalid email format")
      .required("Contact email is required"),
    contactPhone: Yup.string().required("Contact phone is required"),
    address: addressSchema,
  });

  const initialValues: BranchRequestDto = {
    schoolId: school?.id || "",
    name: branch?.name || "",
    branchCode: branch?.branchCode || "",
    licenseKey: "",
    principalName: branch?.principalName || "",
    contactEmail: branch?.contactEmail || "",
    contactPhone: branch?.contactPhone || "",
    address: {
      streetLine1: branch?.address?.streetLine1 || "",
      streetLine2: branch?.address?.streetLine2 || "",
      city: branch?.address?.city || "",
      stateOrProvince: branch?.address?.stateOrProvince || "",
      postalCode: branch?.address?.postalCode || "",
      country: branch?.address?.country || "",
    },
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(branchActions.setErrorUpdate(null))
            : () => dispatch(branchActions.setErrorCreate(null))
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={branchSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;
          const cleanedValues: BranchRequestDto = {
            schoolId: rawCleaned.schoolId || school?.id || null,
            name: rawCleaned.name,
            branchCode: rawCleaned.branchCode,
            licenseKey: rawCleaned.licenseKey,
            principalName: rawCleaned.principalName,
            contactEmail: rawCleaned.contactEmail,
            contactPhone: rawCleaned.contactPhone,
            address: {
              streetLine1: rawCleaned.address.streetLine1,
              streetLine2: rawCleaned.address.streetLine2 || null,
              city: rawCleaned.address.city,
              stateOrProvince: rawCleaned.address.stateOrProvince,
              postalCode: rawCleaned.address.postalCode,
              country: rawCleaned.address.country,
            },
          };

          if (isEditing && branch) {
            dispatch(updateBranch({ id: branch.id, values: cleanedValues }));
          } else {
            dispatch(createBranch(cleanedValues));
          }
        }}
        enableReinitialize
      >
        {({ dirty, isValid }: FormikProps<BranchRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Typography
                variant="h6"
                sx={{ textAlign: "left", fontWeight: 600 }}
              >
                Branch Details
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                <FormTextField name="name" label="Branch Name" />
                <FormTextField name="branchCode" label="Branch Code" />
                {!isEditing && (
                  <FormTextField name="licenseKey" label="License Key" />
                )}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                <FormTextField name="principalName" label="Principal's Name" />
                <FormTextField
                  name="contactEmail"
                  label="Contact Email"
                  type="email"
                />
                <FormTextField name="contactPhone" label="Contact Phone" />
              </Box>

              <Divider sx={{ pt: 1 }}>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, color: "text.secondary" }}
                >
                  Physical Address
                </Typography>
              </Divider>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="address.streetLine1"
                  label="Street Line 1"
                />
                <FormTextField
                  name="address.streetLine2"
                  label="Street Line 2 (Optional)"
                />
                <FormTextField name="address.city" label="City" />
                <FormTextField
                  name="address.stateOrProvince"
                  label="State / Province"
                />
                <FormTextField name="address.postalCode" label="Postal Code" />
                <FormTextField name="address.country" label="Country" />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Branch"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={isEditing ? <SaveOutlinedIcon /> : <BusinessIcon />}
                sx={{ mt: 2 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateBranchForm;
